import React, { useState } from "react";
import { AxiosError } from "axios";
import { Button, CircularProgress, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { packsizeLodash } from "@packsize/utils";
import { ConfirmationModal, Modal } from "@packsize/components";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";

import { getPickZoneSchema, type PickZone } from "@/schemas";
import { type AuditRecord, DiffView } from "@/components/diff-view";
import { usePickZoneConfigurationHistory, useRestorePickZone } from "@/hooks/pick-zones";

interface ConfigurationRestoreButtonProps {
	readonly auditEntry: AuditRecord;
	readonly pickZone: PickZone;
}

const ConfigurationRestoreButton: React.FC<ConfigurationRestoreButtonProps> = ({ pickZone, auditEntry }) => {
	const intl = useIntl();
	const { isPending, mutate: restore, error } = useRestorePickZone();
	const [showRestoreConfirmation, setShowRestoreConfirmation] = useState<boolean>(false);

	const handleRestore = async (data: string): Promise<void> => {
		const rawPickZone = JSON.parse(data);
		const restoreObject = getPickZoneSchema(intl)
			.enrich({ callSite: "ConfigurationRestoreButton - handleRestore" })
			.parseWithTelemetry(packsizeLodash.camelCaseKeys(rawPickZone));

		restore(
			{ pickZoneId: pickZone.id, data: restoreObject },
			{
				onSuccess: () => {
					setShowRestoreConfirmation(false);
				},
			},
		);
	};

	const getErrorMessage = (error: Error | null): string | undefined => {
		if (error === null) {
			return undefined;
		}

		if (error instanceof AxiosError && error.status === 409) {
			return intl.formatMessage({
				id: "Failed to restore Pick Zone. Another Pick zone exists with the same name",
				defaultMessage: "Failed to restore Pick Zone. Another Pick zone exists with the same name",
			});
		}
		return intl.formatMessage({
			id: "Failed to restore Pick Zone. Please try again.",
			defaultMessage: "Failed to restore Pick Zone. Please try again.",
		});
	};

	return (
		<>
			<ConfirmationModal
				title={intl.formatMessage(
					{
						id: "Restore {alias}?",
						defaultMessage: "Restore {alias}?",
					},
					{
						alias: pickZone.alias,
					},
				)}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				confirmText={intl.formatMessage({ id: "Restore", defaultMessage: "Restore" })}
				onCancel={() => setShowRestoreConfirmation(false)}
				onClose={() => setShowRestoreConfirmation(false)}
				onConfirm={() => handleRestore(auditEntry.data)}
				slotProps={{
					confirmButton: {
						"data-testid": "pick-zone-restore-confirmation-modal__button__confirm",
						loading: isPending,
					},
					cancelButton: {
						"data-testid": "pick-zone-restore-confirmation-modal__button__cancel",
					},
				}}
				open={showRestoreConfirmation}
			>
				<Grid container gap={2} direction="column">
					<Typography>
						<FormattedMessage
							id="Are you sure you want to restore this pick zone to this state?"
							defaultMessage="Are you sure you want to restore this pick zone to this state?"
						/>
					</Typography>
					<Typography>
						<FormattedMessage
							id="Pick zone will be restored to a stopped state."
							defaultMessage="Pick zone will be restored to a stopped state."
						/>
					</Typography>
					<Paper variant="outlined" sx={{ padding: 2 }}>
						<Typography variant="caption" whiteSpace="pre-wrap">
							{JSON.stringify(JSON.parse(auditEntry.data), null, 4)}
						</Typography>
					</Paper>
				</Grid>
				{Boolean(error) && (
					<Typography sx={{ marginTop: 2 }} color="error" data-testid="pick-zone-restore-confirmation-modal__error">
						{getErrorMessage(error)}
					</Typography>
				)}
			</ConfirmationModal>
			<Button
				variant="text"
				onClick={() => setShowRestoreConfirmation(true)}
				data-testid="pick-zone-configuration-restore-button__button__open-modal"
				data-pickzoneid={auditEntry.id}
			>
				<FormattedMessage id="Restore version" defaultMessage="Restore version" />
			</Button>
		</>
	);
};

const getActionCell = (row: AuditRecord, rowIndex: unknown, pickZone: PickZone) => {
	if (rowIndex === 0) {
		return null;
	}

	return (
		<Stack direction="row" justifyContent="flex-end">
			<ConfigurationRestoreButton auditEntry={row} pickZone={pickZone} />
		</Stack>
	);
};

interface PickZoneHistoryButtonProps {
	readonly pickZone: PickZone;
}

export const PickZoneHistoryButton: React.FC<PickZoneHistoryButtonProps> = ({ pickZone }) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const historyData = usePickZoneConfigurationHistory(pickZone.id, isOpen);

	const loadingHistoryData = historyData.isLoading || historyData.isFetchedAfterMount === false;

	return (
		<>
			<Modal
				onClose={() => setIsOpen(false)}
				title={intl.formatMessage(
					{
						id: "Pick Zones History: {alias}",
						defaultMessage: "Pick Zones History: {alias}",
					},
					{ alias: pickZone.alias },
				)}
				open={isOpen}
				size="xl"
				slotProps={{
					contentContainer: {
						sx: {
							p: 0,
							overflowY: "scroll",
						},
					},
				}}
			>
				{loadingHistoryData === true && (
					<Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
						<CircularProgress color="info" data-testid="pick-zone-history-button__progress__loading" />
					</Grid>
				)}
				{loadingHistoryData === false && (
					<DiffView
						data={historyData.data}
						actionColumn={{
							name: "",
							maxWidth: "200px",
							cell: (row, rowIndex) => getActionCell(row as AuditRecord, rowIndex, pickZone),
						}}
						autoExpand={false}
					/>
				)}
			</Modal>
			<IconButton
				onClick={() => setIsOpen(true)}
				data-testid="pick-zone-configuration-history-button__button__open-modal"
			>
				<HistoryOutlined />
			</IconButton>
		</>
	);
};
